import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';

/**
 * W7 — resolve the cluster-scoped agent API key from the user's profile.
 *
 * Same lookup Clusters.js uses (handleEditCluster): GET /auth/profile
 * returns the user's active api_keys; we pick the one bound to this
 * cluster. Needed because the agent-facing read feeds the dashboard now
 * surfaces (spot-risk, gitops-snippets) authenticate via X-API-Key, not
 * the dashboard JWT.
 *
 * Returns { apiKey, loading } — apiKey is null while loading, on error,
 * or when the cluster has no active key (callers degrade gracefully).
 */
export default function useClusterApiKey(clusterId) {
  const [apiKey, setApiKey] = useState(null);
  const [loading, setLoading] = useState(Boolean(clusterId));

  useEffect(() => {
    if (!clusterId) {
      setApiKey(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const response = await axios.get(`${API}/auth/profile`);
        if (cancelled) return;
        const key = (response.data?.api_keys || []).find(
          (k) => k.cluster_id === clusterId,
        );
        setApiKey(key?.key || null);
      } catch (error) {
        console.error('Failed to resolve cluster API key:', error);
        if (!cancelled) setApiKey(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clusterId]);

  return { apiKey, loading };
}
